import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'react-toastify';

import { toastConfig } from 'assets/data/config';
import config from '../../../constants/apiConfig';

export type PresentationSlideKind =
  | 'form'
  | 'sitstat'
  | 'cover'
  | 'report'
  | 'briefing'
  | 'blank';

export type PresentationSlide = {
  id: string;
  kind: PresentationSlideKind;
  sourceId: string;
  notes: string;
  body?: string;
  layout?: 'document' | 'quad' | 'widgets';
  widgetCount?: 1 | 4;
  widgetOffset?: number;
  viewLayout?: 'current' | 'quad' | 'framed';
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  dateLine?: string;
  timeLine?: string;
  mark?: 'fema' | null;
  bullets?: string[];
  quadrants?: { title: string; bullets: string[] }[];
  showMap?: boolean;
};

export type IncidentPresentation = {
  incidentId: string;
  slides: PresentationSlide[];
};

const presentationKey = (incidentId: string) =>
  ['incidentPresentation', incidentId] as const;

export function useIncidentPresentation(incidentId: string | undefined) {
  return useQuery<IncidentPresentation>({
    queryKey: presentationKey(incidentId ?? ''),
    queryFn: async () => {
      const { data } = await axios.get(
        config.apiGateway.ics_presentation(incidentId ?? '')
      );
      return data;
    },
    enabled: !!incidentId,
  });
}

export function useSaveIncidentPresentation(incidentId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (slides: PresentationSlide[]) => {
      const { data } = await axios.put(
        config.apiGateway.ics_presentation(incidentId ?? ''),
        {
          slides,
        }
      );
      return data as IncidentPresentation;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(presentationKey(data.incidentId), data);
    },
    onError: (error: {
      response?: { data?: { error?: { message?: string } } };
      message?: string;
    }) => {
      toast.error(
        error?.response?.data?.error?.message ||
          error.message ||
          'Could not save the presentation',
        toastConfig
      );
    },
  });
}

export type HubPresentation = {
  id: string;
  groupGuid: string;
  name: string;
  source: string | null;
  slides: PresentationSlide[];
  updatedAt: string;
};

export type HubPresentationList = {
  presentations: HubPresentation[];
};

const groupPresentationKey = (groupGuid: string | undefined) =>
  ['groupPresentation', groupGuid] as const;

function upsertHubPresentation(
  queryClient: ReturnType<typeof useQueryClient>,
  saved: HubPresentation
) {
  queryClient.setQueryData<HubPresentationList>(
    groupPresentationKey(saved.groupGuid),
    (current) => {
      const presentations = current?.presentations ?? [];
      const next = presentations.some((entry) => entry.id === saved.id)
        ? presentations.map((entry) => (entry.id === saved.id ? saved : entry))
        : [...presentations, saved];
      next.sort(
        (a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id)
      );
      return { presentations: next };
    }
  );
}

function presentationSaveError(
  error: {
    response?: { data?: { error?: { message?: string } } };
    message?: string;
  },
  fallback: string
) {
  toast.error(
    error?.response?.data?.error?.message || error.message || fallback,
    toastConfig
  );
}

export function useHubPresentations(groupGuid: string | undefined) {
  return useQuery<HubPresentationList>({
    queryKey: groupPresentationKey(groupGuid),
    queryFn: async () => {
      const { data } = await axios.get(
        config.apiGateway.group_presentation(groupGuid ?? '')
      );
      return data;
    },
    enabled: !!groupGuid,
  });
}

export function useCreateHubPresentation(groupGuid: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      name: string;
      source?: string | null;
      slides: PresentationSlide[];
    }) => {
      const { data } = await axios.post(
        config.apiGateway.group_presentation(groupGuid ?? ''),
        body
      );
      return data as HubPresentation;
    },
    onSuccess: (saved) => {
      upsertHubPresentation(queryClient, saved);
    },
    onError: (error: {
      response?: { data?: { error?: { message?: string } } };
      message?: string;
    }) => {
      presentationSaveError(error, 'Could not create the presentation');
    },
  });
}

export function useSaveHubPresentation(groupGuid: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      id: string;
      slides: PresentationSlide[];
      name?: string;
    }) => {
      const { data } = await axios.put(
        config.apiGateway.group_presentation_item(groupGuid ?? '', body.id),
        { slides: body.slides, name: body.name }
      );
      return data as HubPresentation;
    },
    onSuccess: (saved) => {
      upsertHubPresentation(queryClient, saved);
    },
    onError: (error: {
      response?: { data?: { error?: { message?: string } } };
      message?: string;
    }) => {
      presentationSaveError(error, 'Could not save the presentation');
    },
  });
}

export function useDeleteHubPresentation(groupGuid: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (presentationId: string) => {
      await axios.delete(
        config.apiGateway.group_presentation_item(
          groupGuid ?? '',
          presentationId
        )
      );
      return presentationId;
    },
    onSuccess: (presentationId) => {
      queryClient.setQueryData<HubPresentationList>(
        groupPresentationKey(groupGuid),
        (current) => ({
          presentations: (current?.presentations ?? []).filter(
            (entry) => entry.id !== presentationId
          ),
        })
      );
    },
    onError: (error: {
      response?: { data?: { error?: { message?: string } } };
      message?: string;
    }) => {
      presentationSaveError(error, 'Could not delete the presentation');
    },
  });
}
